import UserDriverEditParams, {
  UserDriverEditParamsAddress,
  UserDriverEditParamsDriver,
  UserDriverEditParamsTaxiDriverAssociation,
  UserDriverEditParamsTaxiDriverAssociationForB2B,
  UserDriverEditParamsTaxiDriverAssociationForEmployment,
  UserDriverEditParamsUser,
} from "../../../../../../common/services/user/driver/edit/user-driver-edit-params";
import AddressType from "../../../../../../common/types/address-type";
import UserCitizenship from "../../../../../../common/types/user-citizenship";
import UserFormOfEmployment from "../../../../../../common/types/user-form-of-employment";
import UserLanguage from "../../../../../../common/types/user-language";
import UserVehicleOwnership from "../../../../../../common/types/user-vehicle-ownership-form";
import dateService from "../../../../../../common/utils/date/date.service";
import DriverEditAccountFormData from "../form/types/driver-edit-account-form-data";
import DriverEditVehicleFormData from "../form/types/driver-edit-vehicle-form-data";
import RailyDriverEditUserFormData from "../form/types/raily-driver-edit-user-form-data";
import DriverEditAddress from "../types/driver-edit-address";
import DriverEditAddressType from "../types/driver-edit-address-type";
import DriverEditCarOwnershipType from "../types/driver-edit-car-ownership-type";
import DriverEditCitizenshipType from "../types/driver-edit-citizenship-type";
import DriverEditFormOfEmploymentType from "../types/driver-edit-form-of-employment-type";
import DriverEditLanguageType from "../types/driver-edit-language-type";
import DriverEditTaxiCorporationFormData from "./driver-edit-taxi-corporation-form-data";

const driverEditParamsAddressType = (
  type: DriverEditAddressType,
): AddressType => {
  switch (type) {
    case DriverEditAddressType.MAILING:
      return AddressType.MAILING;
    case DriverEditAddressType.STARTING:
      return AddressType.STARTING;
  }
};

const driverEditParamsContractType = (
  type: DriverEditFormOfEmploymentType | null,
): UserFormOfEmployment | undefined => {
  switch (type) {
    case DriverEditFormOfEmploymentType.B2B:
      return UserFormOfEmployment.B2B;
    case DriverEditFormOfEmploymentType.EMPLOYMENT:
      return UserFormOfEmployment.EMPLOYMENT;
    default:
      return undefined;
  }
};

const driverEditParamsCarOwnershipType = (
  type: DriverEditCarOwnershipType,
): UserVehicleOwnership => {
  switch (type) {
    case DriverEditCarOwnershipType.BUSINESS:
      return UserVehicleOwnership.BUSINESS;
    case DriverEditCarOwnershipType.OWNER:
      return UserVehicleOwnership.OWNER;
  }
};

const driverEditParamsCitizenshipType = (
  type: DriverEditCitizenshipType,
): UserCitizenship => {
  switch (type) {
    case DriverEditCitizenshipType.BY:
      return UserCitizenship.BY;
    case DriverEditCitizenshipType.CZ:
      return UserCitizenship.CZ;
    case DriverEditCitizenshipType.DE:
      return UserCitizenship.DE;
    case DriverEditCitizenshipType.LT:
      return UserCitizenship.LT;
    case DriverEditCitizenshipType.PL:
      return UserCitizenship.PL;
    case DriverEditCitizenshipType.RU:
      return UserCitizenship.RU;
    case DriverEditCitizenshipType.SK:
      return UserCitizenship.SK;
    case DriverEditCitizenshipType.UA:
      return UserCitizenship.UA;
  }
};

const driverEditParamsLanguageType = (
  type: DriverEditLanguageType,
): UserLanguage => {
  switch (type) {
    case DriverEditLanguageType.BY:
      return UserLanguage.BY;
    case DriverEditLanguageType.CZ:
      return UserLanguage.CZ;
    case DriverEditLanguageType.DE:
      return UserLanguage.DE;
    case DriverEditLanguageType.LT:
      return UserLanguage.LT;
    case DriverEditLanguageType.PL:
      return UserLanguage.PL;
    case DriverEditLanguageType.RU:
      return UserLanguage.RU;
    case DriverEditLanguageType.SK:
      return UserLanguage.SK;
    case DriverEditLanguageType.UA:
      return UserLanguage.UA;
    case DriverEditLanguageType.EN:
      return UserLanguage.EN;
    case DriverEditLanguageType.ES:
      return UserLanguage.ES;
    case DriverEditLanguageType.FR:
      return UserLanguage.FR;
  }
};

const driverEditParamsLanguagesType = (
  types: DriverEditLanguageType[],
): UserLanguage[] => {
  return types.map(driverEditParamsLanguageType);
};

const createDriverEditParamsAddress = (
  userDataAddress: DriverEditAddress,
): UserDriverEditParamsAddress => {
  const paramsAddress: UserDriverEditParamsAddress = {
    apartment: userDataAddress.apartmentNumber,
    country: userDataAddress.country.trim(),
    description: (userDataAddress.description ?? "").trim(),
    houseNo: userDataAddress.houseNumber.trim(),
    lat: userDataAddress.latitude,
    lon: userDataAddress.longitude,
    street: userDataAddress.street.trim(),
    town: userDataAddress.town.trim(),
    type: driverEditParamsAddressType(userDataAddress.type),
    zipCode: userDataAddress.zipCode.trim(),
  };

  return paramsAddress;
};

const createDriverEditParamsAddresses = (
  userDataAddresses: DriverEditAddress[],
): UserDriverEditParamsAddress[] => {
  return userDataAddresses.map(createDriverEditParamsAddress);
};

const createDriverEditTaxiDriverAssociationForEmploymentRequest = (
  taxiCorporationData: DriverEditTaxiCorporationFormData,
): UserDriverEditParamsTaxiDriverAssociationForEmployment => {
  return {
    contractType: driverEditParamsContractType(
      taxiCorporationData.formOfEmployment,
    )!,
    fleetPartnerId: taxiCorporationData.fleetPartner?.uuid!,
    id: taxiCorporationData.id ? taxiCorporationData.id : "",
  };
};

const createDriverEditTaxiDriverAssociationForB2BRequest = (
  taxiCorporationData: DriverEditTaxiCorporationFormData,
): UserDriverEditParamsTaxiDriverAssociationForB2B => {
  return {
    contractType: driverEditParamsContractType(
      taxiCorporationData.formOfEmployment,
    )!,
    taxId: taxiCorporationData.taxNumber,
    regon: taxiCorporationData.companyId || null,
    krsNo: taxiCorporationData.nationalCourtRegisterNumber || null,
    fleetPartnerId: taxiCorporationData.fleetPartner?.uuid ?? null,
    companyName: taxiCorporationData.name,
    country: taxiCorporationData.country,
    town: taxiCorporationData.town,
    zipCode: taxiCorporationData.zipCode,
    street: taxiCorporationData.street,
    houseNo: taxiCorporationData.houseNumber,
    apartment: taxiCorporationData.apartmentNumber,
    description: taxiCorporationData.description,
    id: taxiCorporationData.id ? taxiCorporationData.id : "",
  };
};

const createDriverEditTaxiDriverAssociationRequest = (
  taxiCorporationData: DriverEditTaxiCorporationFormData,
): UserDriverEditParamsTaxiDriverAssociation => {
  if (
    taxiCorporationData.formOfEmployment === DriverEditFormOfEmploymentType.B2B
  ) {
    return createDriverEditTaxiDriverAssociationForB2BRequest(
      taxiCorporationData,
    );
  }

  return createDriverEditTaxiDriverAssociationForEmploymentRequest(
    taxiCorporationData,
  );
};

const createDriver = (
  userFormData: RailyDriverEditUserFormData,
  vehicleFormData: DriverEditVehicleFormData,
): UserDriverEditParamsDriver => {
  return {
    birthDate: dateService.format(userFormData.birthDate!, "yyyy-mm-dd"),
    birthPlace: userFormData.birthPlace,
    idNumber: userFormData.personalIdNumber,
    citizenship: driverEditParamsCitizenshipType(userFormData.citizenship!),
    languages: driverEditParamsLanguagesType(userFormData.languages),
    experience: userFormData.yearsOfExperience!,
    carOwnership: driverEditParamsCarOwnershipType(vehicleFormData.ownership!),
    carMake: vehicleFormData.make,
    carModel: vehicleFormData.model,
    carRegNo: vehicleFormData.registrationNumber,
    carProdYear: vehicleFormData.productionYear!,
    carSeatsNo: vehicleFormData.numberOfSeats!,
  };
};

const createUser = (
  accountFormData: DriverEditAccountFormData,
  userFormData: RailyDriverEditUserFormData,
): UserDriverEditParamsUser => {
  return {
    firstName: userFormData.firstName,
    lastName: userFormData.lastName,
    mobilePrimaryPrefix: userFormData.mobile.dialingCode ?? "",
    mobilePrimary: userFormData.mobile.number ?? "",
    mobileSecondaryPrefix: userFormData.alternativeMobile.dialingCode,
    mobileSecondary: userFormData.alternativeMobile.number,
    email: accountFormData.email,
  };
};

const createSubmitFormParams = (
  accountFormData: DriverEditAccountFormData,
  userFormData: RailyDriverEditUserFormData,
  vehicleFormData: DriverEditVehicleFormData,
  taxiCorporationData: DriverEditTaxiCorporationFormData[],
): UserDriverEditParams => {
  return {
    user: createUser(accountFormData, userFormData),
    driver: createDriver(userFormData, vehicleFormData),
    associations: taxiCorporationData.map((entry) =>
      createDriverEditTaxiDriverAssociationRequest(entry),
    ),
    addresses: createDriverEditParamsAddresses(userFormData.addresses),
  };
};

const railyDriverEditParamsFactory = {
  createSubmitFormParams,
};

export default railyDriverEditParamsFactory;
